import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { selectUserNotificationById } from "../selectors.js";
import {
  fetchUserNotificationById,
  removeUserNotification,
  saveUserNotification,
} from "../thunks.js";

import UserNotificationForm from "./UserNotificationForm.jsx";

const messages = {
  deleteNotificationConfirmation: {
    id: "modules.UserNotification.components.NotificationDetails.deleteNotificationConfirmation",
    description: "Delete message confirmation",
    defaultMessage: "Are you sure you want to delete this message?",
  },
  deleteNotification: {
    id: "modules.UserNotification.components.NotificationDetails.deleteNotification",
    description: "Delete notification",
    defaultMessage: "Delete Notification",
  },
  editNotification: {
    id: "modules.UserNotification.components.NotificationDetails.editNotification",
    description: "Edit notification",
    defaultMessage: "Edit notification",
  },
  createNotification: {
    id: "modules.UserNotification.components.NotificationDetails.createNotification",
    description: "Create notification",
    defaultMessage: "Create notification",
  },
  notFoundMessage: {
    id: "modules.UserNotifications.components.NotificationDetails.notFoundMessage",
    description: "Error message fetching the notification",
    defaultMessage: "Could not find the notification!",
  },
};

const UserNotificationEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const notification = useSelector((state) =>
    selectUserNotificationById(state, { identifier }),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchUserNotificationById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !notification) {
      fetchData();
    }
  }, [notification, identifier, dispatch]);

  const validate = (values) => {
    const errors = {};
    const required = ["notificationText", "startDate", "endDate"];
    required.forEach((field) => {
      if (!values[field]) errors[field] = "Verplicht";
    });

    if (values.startDate && values.endDate) {
      const start = new Date(values.startDate);
      const end = new Date(values.endDate);
      if (start.getTime() >= end.getTime()) {
        errors.endDate = "Einddatum moet later zijn dan startdatum";
      }
    }
    return errors;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      await dispatch(
        saveUserNotification(
          values,
          notification ? notification.id : undefined,
        ),
      );
      navigate("/notifications", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveNotification = async () => {
    try {
      await dispatch(removeUserNotification(notification.id));
      navigate("/notifications", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!notification && identifier) {
    return <strong>{intl.formatMessage(messages.notFoundMessage)}</strong>;
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {notification
          ? intl.formatMessage(messages.editNotification)
          : intl.formatMessage(messages.createNotification)}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={notification || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={UserNotificationForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteNotification)}
          bodyText={intl.formatMessage(messages.deleteNotificationConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveNotification}
        />
      </Card.Body>
    </Card>
  );
};

export default UserNotificationEditor;
